import { convertQty } from '@trackeat/shared';

export type PurchaseGroup = 'food' | 'beverage' | 'packaging' | 'cleaning' | 'unclassified';
export const PURCHASE_GROUP_LABELS: Record<PurchaseGroup, string> = {
  food: 'Alimenti', beverage: 'Bevande', packaging: 'Packaging', cleaning: 'Detersivi', unclassified: 'Da classificare',
};
export interface PurchaseLine {
  food_id: string; qty: number | null; unit_cost: number | null; line_total: number | null;
  supplier_id: string | null; observed_at: string;
  document_date?: string | null; document_id?: string | null; uom?: string | null;
  currency?: string | null; category?: string | null;
}

const FOOD_CATEGORIES = new Set(['carne', 'pesce', 'verdura', 'frutta', 'latticini', 'cereali', 'pasta_pane', 'salumi', 'condimenti', 'spezie', 'surgelati', 'conserve', 'dolci']);
export function purchaseGroup(category: string | null | undefined): PurchaseGroup {
  if (category === 'bevande') return 'beverage';
  if (category === 'packaging') return 'packaging';
  if (category === 'detersivi') return 'cleaning';
  return category && FOOD_CATEGORIES.has(category) ? 'food' : 'unclassified';
}

/** No import-date or current-price substitution: unknown is not zero. */
export function purchaseDate(line: PurchaseLine): string | null {
  const date = line.document_date?.slice(0, 10);
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const parsed = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : null;
}
export function purchaseAmount(line: PurchaseLine): number | null {
  if ((line.currency ?? 'EUR').toUpperCase() !== 'EUR') return null;
  if (line.line_total != null) return Number.isFinite(line.line_total) && line.line_total >= 0 ? line.line_total : null;
  if (line.qty == null || line.unit_cost == null || !Number.isFinite(line.qty) || !Number.isFinite(line.unit_cost) || line.qty <= 0 || line.unit_cost < 0) return null;
  return line.qty * line.unit_cost;
}
export interface PurchasePeriod { from: string; to: string }
export function purchaseIssues(lines: readonly PurchaseLine[], period: PurchasePeriod, group: PurchaseGroup | 'all'): PurchaseLine[] {
  return lines.filter((line) => {
    const date = purchaseDate(line);
    return (group === 'all' || purchaseGroup(line.category) === group)
      && (!date || ((!period.from || date >= period.from) && (!period.to || date <= period.to)))
      && (!date || purchaseAmount(line) == null);
  });
}
export function selectPurchasePeriod(lines: readonly PurchaseLine[], period: PurchasePeriod): PurchaseLine[] {
  return lines.filter((line) => {
    const date = purchaseDate(line);
    return date != null && (!period.from || date >= period.from) && (!period.to || date <= period.to);
  });
}
export function purchasePeriods(today = new Date()): Record<string, PurchasePeriod> {
  const y = today.getFullYear(), m = today.getMonth();
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    month: { from: day(new Date(y, m, 1)), to: day(today) },
    previous: { from: day(new Date(y, m - 1, 1)), to: day(new Date(y, m, 0)) },
    quarter: { from: day(new Date(y, m - 2, 1)), to: day(today) },
    all: { from: '', to: '' },
  };
}
export function summarizePurchases(lines: readonly PurchaseLine[]) {
  const groups: Record<PurchaseGroup, number> = { food: 0, beverage: 0, packaging: 0, cleaning: 0, unclassified: 0 };
  let amount = 0, valued = 0, missing = 0;
  const bySupplier = new Map<string, number>(), byFood = new Map<string, number>(), byMonth = new Map<string, number>();
  for (const line of lines) {
    const value = purchaseAmount(line);
    if (value == null) { missing++; continue; }
    valued++;
    amount += value;
    groups[purchaseGroup(line.category)] += value;
    const supplier = line.supplier_id ?? '';
    bySupplier.set(supplier, (bySupplier.get(supplier) ?? 0) + value);
    byFood.set(line.food_id, (byFood.get(line.food_id) ?? 0) + value);
    const date = purchaseDate(line);
    if (date) byMonth.set(date.slice(0, 7), (byMonth.get(date.slice(0, 7)) ?? 0) + value);
  }
  return { amount, valued, missing, groups, bySupplier, byFood, byMonth, total: lines.length };
}

/** Compare like-for-like receipt prices, weighted by quantity in the base unit. */
export function purchasePriceChanges(lines: readonly PurchaseLine[], canonicalUom: (id: string) => string | null) {
  const groups = new Map<string, Array<{ date: string; qty: number; amount: number; supplierId: string | null; documentId: string | null }>>();
  for (const line of lines) {
    const date = purchaseDate(line), amount = purchaseAmount(line), base = canonicalUom(line.food_id);
    const qty = line.qty != null && line.uom && base ? convertQty(line.qty, line.uom, base) : null;
    if (!date || amount == null || qty == null || qty <= 0) continue;
    const entries = groups.get(line.food_id) ?? [];
    entries.push({ date, amount, qty, supplierId: line.supplier_id, documentId: line.document_id ?? null });
    groups.set(line.food_id, entries);
  }
  return [...groups].flatMap(([foodId, entries]) => {
    const days = new Map<string, { amount: number; qty: number }>();
    for (const entry of entries) {
      const value = days.get(entry.date) ?? { amount: 0, qty: 0 };
      value.amount += entry.amount; value.qty += entry.qty; days.set(entry.date, value);
    }
    const ordered = [...days].sort(([a], [b]) => a.localeCompare(b));
    if (ordered.length < 2) return [];
    const [previousDate, previous] = ordered[ordered.length - 2], [date, latest] = ordered[ordered.length - 1];
    const before = previous.amount / previous.qty, current = latest.amount / latest.qty;
    if (before <= 0) return [];
    const documents = [...new Set(entries.filter((entry) => entry.date === date).map((entry) => entry.documentId))];
    return [{ foodId, previousDate, date, before, current, pct: (current / before - 1) * 100,
      impact: (current - before) * latest.qty, qty: latest.qty, uom: canonicalUom(foodId)!,
      documentId: documents.length === 1 ? documents[0] : null }];
  }).sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
}
